import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  TextField, MenuItem, Divider, Avatar, List, ListItem,
  ListItemText, DialogActions, Paper, Stack, Grid, Card, 
  CardContent, CardMedia, ToggleButton, ToggleButtonGroup, IconButton,
  CircularProgress
} from "@mui/material";

import {
  AllInclusive as AllPhotosIcon,
  Timeline as TimelineIcon,
  ChatBubbleOutline as ChatIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Close as CloseIcon,
  PlayCircleOutline as PlayIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon
} from "@mui/icons-material";

import Swal from "sweetalert2";
import { db, auth, storage } from "../../../firebase";
import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDocs,
  query, orderBy, serverTimestamp, arrayUnion, arrayRemove,
  getDoc
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const PRIMARY_INDIGO = "#6366F1";

const isVideoFile = (url) => {
  if (!url) return false;
  const cleanUrl = url.split("?")[0].toLowerCase();
  return cleanUrl.endsWith(".mp4") || cleanUrl.endsWith(".webm") || cleanUrl.endsWith(".ogg") || cleanUrl.includes("video");
};

const CommentItem = ({ comment }) => {
  const [userName, setUserName] = useState("Loading...");

  useEffect(() => {
    const fetchUserName = async () => {
      if (!comment.userId) return;
      try {
        const userDoc = await getDoc(doc(db, "users", comment.userId));
        if (userDoc.exists()) {
          setUserName(userDoc.data().name || "Anonymous");
        } else {
          setUserName("Unknown User");
        }
      } catch (err) {
        setUserName("User");
      }
    };
    fetchUserName();
  }, [comment.userId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <ListItem alignItems="flex-start" sx={{ px: 1.5, py: 0.75, mb: 1, bgcolor: 'white', borderRadius: 2, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
      <Avatar sx={{ width: 28, height: 28, mr: 1.5, bgcolor: PRIMARY_INDIGO, fontSize: '0.75rem' }}>
        {userName.charAt(0).toUpperCase()}
      </Avatar>
      <ListItemText
        primary={
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" fontWeight="700" color="text.primary">
              {userName}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
              {formatDate(comment.createdAt)}
            </Typography>
          </Stack>
        }
        secondary={
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.3, fontSize: '0.8rem' }}>
            {comment.text}
          </Typography>
        }
      />
    </ListItem>
  );
};

function Dailyposts() {
  const [posts, setPosts] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [viewMode, setViewMode] = useState("timeline");
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);

  // Dialog Controls
  const [open, setOpen] = useState(false); 
  const [detailOpen, setDetailOpen] = useState(false); 
  const [activePost, setActivePost] = useState(null);
  const [commentText, setCommentText] = useState("");

  const [form, setForm] = useState({
    title: "", content: "", files: [], postType: "", subCategory: "",
    date: new Date().toISOString().split("T")[0]
  });
  const [previewFiles, setPreviewFiles] = useState([]);
  const [editId, setEditId] = useState(null);

  const selectedCategoryObj = categoriesData.find(c => c.title === form.postType);
  const availableSubCategories = selectedCategoryObj?.subcategories || [];

  useEffect(() => {
    fetchInitialData();
    fetchPosts();
  }, []);

  const fetchInitialData = async () => {
    try {
      const typeSnap = await getDocs(query(collection(db, "post_types"), orderBy("createdAt", "asc")));
      setCategoriesData(typeSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "dailyposts"), orderBy("date", "desc"));
      const snap = await getDocs(q);
      setPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleLike = async (post) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;
    const isLiked = post.likes?.includes(userId);
    const postRef = doc(db, "dailyposts", post.id);

    await updateDoc(postRef, {
      likes: isLiked ? arrayRemove(userId) : arrayUnion(userId)
    });

    if (activePost && activePost.id === post.id) {
      setActivePost({
        ...post,
        likes: isLiked ? post.likes.filter(id => id !== userId) : [...(post.likes || []), userId]
      });
    }
    fetchPosts();
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !activePost) return;
    const newComment = {
      text: commentText,
      userName: auth.currentUser?.displayName || "User",
      userId: auth.currentUser?.uid,
      createdAt: new Date().toISOString()
    };
    await updateDoc(doc(db, "dailyposts", activePost.id), { comments: arrayUnion(newComment) });
    setCommentText("");
    setActivePost({ ...activePost, comments: [...(activePost.comments || []), newComment] });
    fetchPosts();
  };

  const handleSubmit = async () => {
    if (!form.title || !form.postType) return Swal.fire("Error", "Missing fields", "error");
    setSubmitLoading(true);
    try {
      let finalUrls = editId ? [...previewFiles] : [];
      
      // If new files are being added during create or edit sequence
      if (form.files.length > 0) {
        const uploads = [];
        for (const file of form.files) {
          const sRef = ref(storage, `posts/${Date.now()}_${file.name}`);
          await uploadBytes(sRef, file);
          uploads.push(await getDownloadURL(sRef));
        }
        // In edit mode, append new uploads to existing unremoved media URLs
        finalUrls = editId ? [...previewFiles.filter(url => !url.startsWith('blob:')), ...uploads] : uploads;
      }

      const data = {
        title: form.title, content: form.content, postType: form.postType,
        subCategory: form.subCategory, date: form.date, files: finalUrls,
        updatedAt: serverTimestamp(), userId: auth.currentUser?.uid
      };

      if (editId) { 
        await updateDoc(doc(db, "dailyposts", editId), data); 
        Swal.fire("Updated!", "Your activity has been modified.", "success");
      } else {
        data.createdAt = serverTimestamp(); data.likes = []; data.comments = [];
        await addDoc(collection(db, "dailyposts"), data);
        Swal.fire("Saved!", "New activity registered successfully.", "success");
      }
      setOpen(false); resetForm(); fetchPosts();
    } catch (err) { Swal.fire("Error", err.message, "error"); }
    setSubmitLoading(false);
  };

  const handleEditInit = (post, e) => {
    e.stopPropagation(); // Stop card click routing
    setEditId(post.id);
    setForm({
      title: post.title,
      content: post.content || "",
      postType: post.postType,
      subCategory: post.subCategory || "",
      date: post.date,
      files: [] // Holds newly queued structural files
    });
    setPreviewFiles(post.files || []);
    setOpen(true);
  };

  const handleDeletePost = async (postId, e) => {
    e.stopPropagation();
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This process cannot be reverted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#64748B",
      confirmButtonText: "Yes, delete it!"
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, "dailyposts", postId));
        Swal.fire("Deleted!", "Post structure successfully expunged.", "success");
        fetchPosts();
      } catch (err) {
        Swal.fire("Error", "Could not complete deletion action.", "error");
      }
    }
  };

  const resetForm = () => {
    setForm({ title: "", content: "", files: [], postType: "", subCategory: "", date: selectedDate });
    setPreviewFiles([]); setEditId(null);
  };

  const groupedData = posts.reduce((acc, post) => {
    if (post.files) {
      if (!acc[post.date]) acc[post.date] = [];
      post.files.forEach(url => acc[post.date].push({ url, post }));
    }
    return acc;
  }, {});

  return (
    <Box p={1} sx={{ backgroundColor: "#F8FAFC", minHeight: "100vh" }}>
      {/* COMPACT HEADER */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 2, border: "1px solid #E2E8F0" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="900">Activity</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <ToggleButtonGroup value={viewMode} exclusive onChange={(e, v) => v && setViewMode(v)} size="small">
              <ToggleButton value="timeline" sx={{ p: 0.5 }}><TimelineIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="all-gallery" sx={{ p: 0.5 }}><AllPhotosIcon fontSize="small" /></ToggleButton>
            </ToggleButtonGroup>
            <TextField type="date" size="small" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} sx={{ width: 140 }} />
            <Button variant="contained" size="small" onClick={() => { resetForm(); setOpen(true); }} sx={{ bgcolor: PRIMARY_INDIGO, px: 2 }}>Post</Button>
          </Stack>
        </Stack>
      </Paper>

      {/* VIEW LOADING SPINNER */}
      {loading ? (
        <Stack alignItems="center" py={5}><CircularProgress size={30} sx={{ color: PRIMARY_INDIGO }} /></Stack>
      ) : (
        <>
          {/* VIEW MODES */}
          {viewMode === "timeline" ? (
            <Grid container spacing={1.5}>
              {posts.filter(p => p.date === selectedDate).map(post => (
                <Grid item xs={12} sm={6} md={4} key={post.id}>
                  <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', position: 'relative' }}>
                    
                    {/* EDIT & DELETE OPTIONS */}
                    <Box sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2, display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={(e) => handleEditInit(post, e)} sx={{ bgcolor: 'white', boxShadow: 1, '&:hover': { bgcolor: '#F1F5F9' } }}>
                        <EditIcon sx={{ fontSize: 14, color: PRIMARY_INDIGO }} />
                      </IconButton>
                      <IconButton size="small" onClick={(e) => handleDeletePost(post.id, e)} sx={{ bgcolor: 'white', boxShadow: 1, '&:hover': { bgcolor: '#FEF2F2' } }}>
                        <DeleteIcon sx={{ fontSize: 14, color: '#EF4444' }} />
                      </IconButton>
                    </Box>

                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" fontWeight="600" color="primary">{post.postType}</Typography>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ pr: 6 }} gutterBottom>{post.title}</Typography>
                      
                      <Grid container spacing={0.5} sx={{ mt: 0.5 }}>
                        {post.files?.map((fileUrl, i) => (
                          <Grid item xs={4} key={i}>
                            {isVideoFile(fileUrl) ? (
                              <Box 
                                onClick={() => { setActivePost(post); setDetailOpen(true); }}
                                sx={{ height: 65, borderRadius: 1.5, cursor: 'pointer', bgcolor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}
                              >
                                <Box component="video" src={fileUrl} sx={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.6 }} />
                                <PlayIcon sx={{ position: 'absolute', color: '#fff', fontSize: 24 }} />
                              </Box>
                            ) : (
                              <CardMedia
                                component="img" image={fileUrl}
                                onClick={() => { setActivePost(post); setDetailOpen(true); }}
                                sx={{ height: 65, borderRadius: 1.5, cursor: 'pointer', objectFit: 'cover' }}
                              />
                            )}
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            Object.keys(groupedData).sort((a, b) => b.localeCompare(a)).map(date => (
              <Box key={date} mb={2}>
                <Typography variant="caption" fontWeight="bold" sx={{ opacity: 0.6, mb: 0.5, display: 'block' }}>{date}</Typography>
                <Grid container spacing={0.5}>
                  {groupedData[date].map((item, i) => (
                    <Grid item xs={3} sm={2} md={1.5} key={i}>
                      {isVideoFile(item.url) ? (
                        <Box 
                          onClick={() => { setActivePost(item.post); setDetailOpen(true); }}
                          sx={{ aspectRatio: '1/1', borderRadius: 1, cursor: 'pointer', bgcolor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}
                        >
                          <Box component="video" src={item.url} sx={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.6 }} />
                          <PlayIcon sx={{ position: 'absolute', color: '#fff', fontSize: 20 }} />
                        </Box>
                      ) : (
                        <CardMedia
                          component="img" image={item.url}
                          onClick={() => { setActivePost(item.post); setDetailOpen(true); }}
                          sx={{ aspectRatio: '1/1', borderRadius: 1, cursor: 'pointer', objectFit: 'cover' }}
                        />
                      )}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))
          )}
        </>
      )}

      {/* --- REFACTORED COMPACT IMAGE & VIDEO DETAIL POPUP --- */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="sm">
        <IconButton size="small" onClick={() => setDetailOpen(false)} sx={{ position: 'absolute', right: 6, top: 6, zIndex: 1, bgcolor: 'rgba(255,255,255,0.8)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
        <DialogContent sx={{ p: 0 }}>
          <Stack direction="column">
            <Box sx={{ bgcolor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '260px', width: '100%' }}>
              {isVideoFile(activePost?.files?.[0]) ? (
                <Box component="video" src={activePost?.files?.[0]} controls autoPlay sx={{ width: '100%', height: '100%', maxHeight: '260px', objectFit: 'contain' }} />
              ) : (
                <CardMedia component="img" image={activePost?.files?.[0]} sx={{ width: '100%', height: '100%', maxHeight: '260px', objectFit: 'contain' }} />
              )}
            </Box>

            <Box p={1.5}>
              <Typography variant="subtitle2" fontWeight="bold">{activePost?.title}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{activePost?.content}</Typography>
              
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  startIcon={activePost?.likes?.includes(auth.currentUser?.uid) ? <FavoriteIcon fontSize="small" color="error" /> : <FavoriteBorderIcon fontSize="small" />}
                  onClick={() => handleLike(activePost)}
                  sx={{ fontSize: '0.75rem', py: 0 }}
                >
                  {activePost?.likes?.length || 0} Likes
                </Button>
                <Typography variant="caption" color="text.disabled">• {activePost?.comments?.length || 0} Comments</Typography>
              </Stack>
            </Box>

            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1.5, bgcolor: '#F1F5F9', maxHeight: '180px', borderTop: '1px solid #E2E8F0', borderBottom: '1px solid #E2E8F0' }}>
              {activePost?.comments && activePost.comments.length > 0 ? (
                <List sx={{ p: 0 }}>
                  {activePost.comments.map((c, i) => <CommentItem key={i} comment={c} />)}
                </List>
              ) : (
                <Stack alignItems="center" justifyContent="center" sx={{ py: 2, opacity: 0.5 }}>
                  <ChatIcon sx={{ fontSize: 24, mb: 0.5 }} />
                  <Typography variant="caption">No comments yet.</Typography>
                </Stack>
              )}
            </Box>

            <Box p={1}>
              <Stack direction="row" spacing={1}>
                <TextField
                  fullWidth size="small" placeholder="Add a comment..."
                  value={commentText} onChange={(e) => setCommentText(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                  inputProps={{ style: { fontSize: 13, padding: '7px 10px' } }}
                />
                <Button size="small" onClick={handleAddComment} variant="contained" sx={{ bgcolor: PRIMARY_INDIGO, px: 2 }}>Post</Button>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* COMPACT MODAL FORM WITH LOADING OPTION */}
      <Dialog open={open} onClose={() => !submitLoading && setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ p: 1.5, fontSize: '1rem', fontWeight: 'bold' }}>
          {editId ? "Modify Activity" : "Create Activity"}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1.5 }}>
          <Stack spacing={1.5}>
            <TextField fullWidth size="small" label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} disabled={submitLoading} />
            
            <TextField
              select fullWidth size="small" label="Category" value={form.postType}
              onChange={(e) => setForm({ ...form, postType: e.target.value, subCategory: "" })}
              disabled={submitLoading}
            >
              {categoriesData.map(c => (
                <MenuItem key={c.id} value={c.title} sx={{ fontSize: '0.85rem' }}>{c.title}</MenuItem>
              ))}
            </TextField>

            <TextField
              select fullWidth size="small" label="Sub Category" value={form.subCategory}
              disabled={!form.postType || availableSubCategories.length === 0 || submitLoading}
              onChange={(e) => setForm({ ...form, subCategory: e.target.value })}
              helperText={!form.postType ? "Select a category first" : ""}
            >
              {availableSubCategories.map((sub, index) => (
                <MenuItem key={index} value={sub} sx={{ fontSize: '0.85rem' }}>{sub}</MenuItem>
              ))}
            </TextField>
            <TextField type="date" fullWidth size="small" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} disabled={submitLoading} />
            <TextField fullWidth multiline rows={2} size="small" label="Content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} disabled={submitLoading} />
            
            <Button variant="outlined" size="small" component="label" startIcon={<CloudUploadIcon />} sx={{ textTransform: 'none' }} disabled={submitLoading}>
              Upload Photos / Videos
              <input type="file" hidden multiple accept="image/*,video/*" onChange={(e) => {
                const files = Array.from(e.target.files);
                setForm({ ...form, files });
                setPreviewFiles(files.map(f => URL.createObjectURL(f)));
              }} />
            </Button>

            {/* Micro Thumbnail File Previews */}
            <Grid container spacing={0.5}>
              {previewFiles.map((url, i) => (
                <Grid item xs={3} key={i} sx={{ position: 'relative' }}>
                  {isVideoFile(form.files[i]?.name || url) ? (
                    <Box sx={{ height: 45, borderRadius: 1, bgcolor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <PlayIcon sx={{ color: '#fff', fontSize: 16 }} />
                    </Box>
                  ) : (
                    <CardMedia component="img" image={url} sx={{ height: 45, borderRadius: 1, objectFit: 'cover' }} />
                  )}
                </Grid>
              ))}
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 1 }}>
          <Button size="small" onClick={() => setOpen(false)} disabled={submitLoading}>Cancel</Button>
          <Button 
            size="small" 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={submitLoading}
            sx={{ bgcolor: PRIMARY_INDIGO, minWidth: 80 }}
          >
            {submitLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Dailyposts;